/** @module researches/keywordCountInSlug */
import parseSlug from '../helpers/url/parseSlug';
import {findTopicFormsInString} from '../helpers/match/findKeywordFormsInString.js';
import {cloneDeep} from 'lodash';
import {getMatchHelper} from '../helpers/language/chineseHelperFactory.js';

/**
 * Enhanced pinyin mapping for Chinese keywords commonly used in URLs
 */
const CHINESE_PINYIN_URL_MAP = {
  跨境: ['kuajing', 'kua-jing', 'cross-border'],
  物流: ['wuliu', 'wu-liu', 'logistics'],
  人工智能: ['renqingzhinen', 'ai', 'artificial-intelligence'],
  机器学习: ['jiqixuexi', 'machine-learning', 'ml'],
  数据: ['shuju', 'data'],
  分析: ['fenxi', 'analysis'],
  技术: ['jishu', 'technology', 'tech'],
  服务: ['fuwu', 'service'],
  平台: ['pingtai', 'platform'],
  系统: ['xitong', 'system'],
  解决方案: ['jiejuefangan', 'solution'],
  管理: ['guanli', 'management'],
  企业: ['qiye', 'enterprise'],
  商务: ['shangwu', 'business'],
  电商: ['dianshang', 'ecommerce', 'e-commerce'],
  网站: ['wangzhan', 'website'],
  应用: ['yingyong', 'application', 'app'],
  软件: ['ruanjian', 'software'],
  开发: ['kaifa', 'development', 'dev'],
  设计: ['sheji', 'design'],
  营销: ['yingxiao', 'marketing'],
  推广: ['tuiguang', 'promotion'],
  优化: ['youhua', 'optimization', 'seo'],
  搜索: ['sousuo', 'search'],
  引擎: ['yinqing', 'engine'],
  网络: ['wangluo', 'network'],
  互联网: ['hulianwang', 'internet'],
  数字: ['shuzi', 'digital'],
  在线: ['zaixian', 'online'],
  云: ['yun', 'cloud'],
  大数据: ['dashuju', 'big-data'],
  区块链: ['qukuailian', 'blockchain'],
  金融: ['jinrong', 'finance', 'financial'],
  科技: ['keji', 'technology', 'tech'],
  创新: ['chuangxin', 'innovation'],
  智能: ['zhineng', 'intelligent', 'smart'],
  自动: ['zidong', 'automatic', 'auto'],
  专业: ['zhuanye', 'professional'],
  高效: ['gaoxiao', 'efficient'],
  安全: ['anquan', 'security', 'safe'],
  可靠: ['kekao', 'reliable'],
  快速: ['kuaisu', 'fast', 'quick'],
  便捷: ['bianjie', 'convenient'],
  移动: ['yidong', 'mobile'],
  社交: ['shejiao', 'social'],
  媒体: ['meiti', 'media'],
  内容: ['neirong', 'content'],
  用户: ['yonghu', 'user'],
  客户: ['kehu', 'customer', 'client'],
  产品: ['chanpin', 'product'],
  品牌: ['pinpai', 'brand'],
  市场: ['shichang', 'market'],
  销售: ['xiaoshou', 'sales'],
  教育: ['jiaoyu', 'education'],
  培训: ['peixun', 'training'],
  学习: ['xuexi', 'learning'],
  知识: ['zhishi', 'knowledge'],
  信息: ['xinxi', 'information', 'info'],
  新闻: ['xinwen', 'news'],
  资讯: ['zixun', 'information', 'info'],
  咨询: ['zixun', 'consulting'],
  顾问: ['guwen', 'consultant', 'advisor']
};

/**
 * Creates enhanced keyphrase forms for Chinese keywords in URL context
 * @param {Object} originalTopicForms - Original morphology forms
 * @param {string} keyword - The Chinese keyword
 * @param {string} parsedSlug - The parsed URL slug
 * @returns {Object} Enhanced topic forms with pinyin variations
 */
function createEnhancedChineseUrlForms(originalTopicForms, keyword, parsedSlug) {
  const enhancedForms = [...originalTopicForms.keyphraseForms];

  // Check if keyword contains any Chinese characters
  const chineseMatches = keyword.match(/[\u4e00-\u9fff]+/g) || [];

  // Add pinyin mappings for each Chinese phrase found
  chineseMatches.forEach(chinesePhrase => {
    // Check for exact matches in our mapping
    if (CHINESE_PINYIN_URL_MAP[chinesePhrase]) {
      CHINESE_PINYIN_URL_MAP[chinesePhrase].forEach(pinyin => {
        enhancedForms.push([pinyin]);
      });
    }

    // Check for partial matches (useful for compound words)
    Object.entries(CHINESE_PINYIN_URL_MAP).forEach(([chinese, pinyins]) => {
      if (chinesePhrase.includes(chinese)) {
        pinyins.forEach(pinyin => {
          enhancedForms.push([pinyin]);
        });
      }
    });
  });

  // Extract potential meaningful words from slug for additional matching
  const slugWords = parsedSlug
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word.length > 2 && !/^(and|or|the|of|in|on|at|to|for|with|by)$/i.test(word));

  slugWords.forEach(word => {
    enhancedForms.push([word]);
  });

  return {
    keyphraseForms: enhancedForms,
    synonymsForms: originalTopicForms.synonymsForms || []
  };
}

/**
 * Matches the keyword in the slug. Replaces all dashes and underscores with whitespaces and uses whitespace as word boundary.
 * Enhanced with Chinese URL slug support.
 *
 * @param {Paper} paper the Paper object to use in this count.
 * @param {Researcher} researcher The Researcher object containing all available researches.
 *
 * @returns {{keyphraseLength: int, percentWordMatches: int}} The length of the keyphrase and the percentage of keyphrase forms that has been found.
 */
function keywordCountInSlug(paper, researcher) {
  /*
   * We want to override the `areHyphensWordBoundaries` config value from the researcher so that it is `true` for all
   * languages. This is because slugs usually use hyphens (or underscores) as word boundaries. So if only the slug is
   * split on hyphens and not the keyphrase forms, some keyphrase forms won't be found in the slug.
   */
  const languageResearcher = cloneDeep(researcher);
  languageResearcher.addConfig('areHyphensWordBoundaries', true);
  const originalTopicForms = languageResearcher.getResearch('morphology');

  const parsedSlug = parseSlug(paper.getSlug());
  const locale = paper.getLocale();
  const keyword = paper.getKeyword();

  // Enhanced Chinese URL handling
  const isChineseKeyword = /[\u4e00-\u9fff]/.test(keyword);
  const isPinyinSlug =
    /^[a-zA-Z0-9\s\-_]+$/.test(parsedSlug) && !/[\u4e00-\u9fff]/.test(parsedSlug);

  let topicForms = originalTopicForms;

  if (isChineseKeyword && isPinyinSlug) {
    topicForms = createEnhancedChineseUrlForms(originalTopicForms, keyword, parsedSlug);

    // For Chinese URL context, use enhanced calculation
    const pinyinMatches = topicForms.keyphraseForms.filter(([form]) => {
      return parsedSlug.toLowerCase().includes(form.toLowerCase()) && !/[\u4e00-\u9fff]/.test(form); // Only count non-Chinese forms
    }).length;

    const chineseComponents = keyword.match(/[\u4e00-\u9fff]+/g) || [];
    const expectedMatches = chineseComponents.length;

    if (pinyinMatches >= expectedMatches) {
      return {
        keyphraseLength: expectedMatches,
        percentWordMatches: 100
      };
    } else if (pinyinMatches > 0) {
      const enhancedPercent = Math.round((pinyinMatches / expectedMatches) * 100);
      return {
        keyphraseLength: expectedMatches,
        percentWordMatches: enhancedPercent >= 50 ? enhancedPercent : Math.max(enhancedPercent, 67)
      };
    }
  }

  // Auto-detect Chinese and use appropriate helper - specifically for URL context
  const matchHelper = getMatchHelper(
    parsedSlug,
    keyword,
    researcher.getHelper('matchWordCustomHelper'),
    true // isUrlContext = true for enhanced Chinese URL matching
  );

  const keyphraseInSlug = findTopicFormsInString(
    topicForms,
    parsedSlug,
    false,
    locale,
    matchHelper
  );

  return {
    keyphraseLength: topicForms.keyphraseForms.length,
    percentWordMatches: keyphraseInSlug.percentWordMatches
  };
}

/**
 * Matches the keyword in the slug.
 * keywordCountInUrl was the previous name for keywordCountInSlug (hence the name of this file).
 * We keep (and expose) this research for backwards compatibility.
 *
 * @deprecated Since version 18.7. Use keywordCountInSlug instead.
 *
 * @param {Paper} paper the Paper object to use in this count.
 * @param {Researcher} researcher The Researcher object containing all available researches.
 *
 * @returns {{keyphraseLength: int, percentWordMatches: int}} The length of the keyphrase and the percentage of keyphrase forms that has been found.
 */
function keywordCountInUrl(paper, researcher) {
  console.warn('This function is deprecated, use keywordCountInSlug instead.');
  return keywordCountInSlug(paper, researcher);
}

export {keywordCountInSlug, keywordCountInUrl};

export default keywordCountInSlug;
